import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AntLocalization from '../../utils/AntLocalization';
import AntTime from '../../utils/AntTime';
import AntCondition from '../../utils/AntCondition';
import ResourcePanel from './ResourcePanel';
import TimelineBlock from './TimelineBlock';
import './index.scss';

// ─── Date/Time helpers ────────────────────────────────────────
function startOfDay(d) {
    var r = new Date(d); r.setHours(0, 0, 0, 0); return r;
}

function addDays(d, n) {
    var r = new Date(d); r.setDate(r.getDate() + n); return r;
}

function addMonths(d, n) {
    var r = new Date(d); r.setMonth(r.getMonth() + n); return r;
}

function formatDate(d, mode) {
    if (!d) return '';
    var dd = d.getDate();
    var mm = d.getMonth() + 1;
    if (mode === 'month') return (dd < 10 ? '0' : '') + dd;
    var dayNames = { vi: ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'], en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] };
    var lang = 'vi';
    try { lang = AntLocalization.getLanguage() || 'vi'; } catch (e) { /* */ }
    var names = dayNames[lang] || dayNames.vi;
    var dayName = names[d.getDay()];
    return dayName + ' ' + dd + '/' + mm;
}

function formatHour(h) {
    var hh = Math.floor(h);
    var mm = Math.round((h - hh) * 60);
    return (hh < 10 ? '0' : '') + hh + ':' + (mm < 10 ? '0' : '') + mm;
}

function parseTimeToHours(value) {
    if (value == null) return null;
    if (typeof value === 'number') {
        // Unix timestamp → extract hours
        if (value > 100000) {
            var d = new Date(value * 1000);
            return d.getHours() + d.getMinutes() / 60;
        }
        return value;
    }
    if (typeof value === 'string') {
        // "HH:mm" or "HH:mm:ss"
        var parts = value.split(':');
        return parseInt(parts[0], 10) + (parseInt(parts[1] || '0', 10) / 60);
    }
    return null;
}

function parseDateValue(value) {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'number') {
        // Unix timestamp (seconds or milliseconds)
        if (value < 1e12) return new Date(value * 1000);
        return new Date(value);
    }
    if (typeof value === 'string') return new Date(value);
    return null;
}

function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isToday(d) {
    return isSameDay(d, new Date());
}

function isWeekend(d) {
    var day = d.getDay();
    return day === 0 || day === 6;
}

// ─── Generate time columns ───────────────────────────────────
function generateDayColumns(startHour, endHour, slotMinutes) {
    var cols = [];
    var step = slotMinutes / 60;
    for (var h = startHour; h < endHour; h += step) {
        cols.push({
            key: 'h_' + h,
            label: formatHour(h),
            start: h,
            end: h + step,
            isNow: false
        });
    }
    // Mark current hour
    var now = new Date();
    var currentH = now.getHours() + now.getMinutes() / 60;
    for (var i = 0; i < cols.length; i++) {
        if (currentH >= cols[i].start && currentH < cols[i].end) {
            cols[i].isNow = true;
            break;
        }
    }
    return cols;
}

function generateWeekColumns(baseDate) {
    var cols = [];
    var start = startOfDay(baseDate);
    // Lùi về thứ 2 (Monday)
    var dayOfWeek = start.getDay();
    var diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    start = addDays(start, diff);

    for (var i = 0; i < 7; i++) {
        var d = addDays(start, i);
        cols.push({
            key: 'd_' + i,
            label: formatDate(d, 'week'),
            date: d,
            isToday: isToday(d),
            isWeekend: isWeekend(d)
        });
    }
    return cols;
}

function generateMonthColumns(baseDate) {
    var cols = [];
    var year = baseDate.getFullYear();
    var month = baseDate.getMonth();
    var daysInMonth = new Date(year, month + 1, 0).getDate();

    for (var i = 1; i <= daysInMonth; i++) {
        var d = new Date(year, month, i);
        cols.push({
            key: 'd_' + i,
            label: String(i),
            date: d,
            dayOfWeek: d.getDay(),
            isToday: isToday(d),
            isWeekend: isWeekend(d)
        });
    }
    return cols;
}

// ─── Position calculator ──────────────────────────────────────
function calcBlockPosition(item, columns, mode, startField, endField, startHour, endHour) {
    var totalCols = columns.length;
    if (totalCols === 0) return null;

    if (mode === 'day') {
        var sH = parseTimeToHours(item[startField]);
        var eH = endField ? parseTimeToHours(item[endField]) : null;
        if (sH == null) return null;
        if (eH == null) eH = sH + 1; // Default 1 hour

        var range = endHour - startHour;
        var left = ((sH - startHour) / range) * 100;
        var width = ((eH - sH) / range) * 100;
        if (left < 0) { width += left; left = 0; }
        if (left + width > 100) width = 100 - left;
        if (width <= 0) return null;
        return { left: left, width: width };
    }

    if (mode === 'week' || mode === 'month') {
        var startDate = parseDateValue(item[startField]);
        var endDate = endField ? parseDateValue(item[endField]) : null;
        if (!startDate) return null;
        if (!endDate) endDate = addDays(startDate, 1);

        var firstCol = columns[0].date;
        var lastCol = columns[columns.length - 1].date;
        var rangeMs = addDays(lastCol, 1).getTime() - firstCol.getTime();

        var sMs = Math.max(startDate.getTime(), firstCol.getTime()) - firstCol.getTime();
        var eMs = Math.min(endDate.getTime(), addDays(lastCol, 1).getTime()) - firstCol.getTime();

        var left2 = (sMs / rangeMs) * 100;
        var width2 = ((eMs - sMs) / rangeMs) * 100;
        if (width2 <= 0) return null;
        return { left: left2, width: width2 };
    }

    return null;
}

// ─── Current time indicator ───────────────────────────────────
function CurrentTimeIndicator({ mode, columns, startHour, endHour, baseDate }) {
    if (mode === 'day') {
        var now = new Date();
        var ch = now.getHours() + now.getMinutes() / 60;
        if (ch < startHour || ch > endHour) return null;
        var pct = ((ch - startHour) / (endHour - startHour)) * 100;
        return <div className="current-time-line" style={{ left: pct + '%' }} />;
    }
    if (mode === 'month' || mode === 'week') {
        var today = new Date();
        for (var i = 0; i < columns.length; i++) {
            if (columns[i].date && isSameDay(columns[i].date, today)) {
                var leftPct = (i / columns.length) * 100;
                var widthPct = (1 / columns.length) * 100;
                return <div className="current-day-highlight" style={{ left: leftPct + '%', width: widthPct + '%' }} />;
            }
        }
    }
    return null;
}

// ─── Navigation Bar ───────────────────────────────────────────
function TimelineNav({ mode, baseDate, onPrev, onNext, onToday, onModeChange, modes }) {
    var lang = 'vi';
    try { lang = AntLocalization.getLanguage() || 'vi'; } catch (e) { /* */ }

    var label = '';
    if (mode === 'day') {
        label = formatDate(baseDate, 'day');
        var fullDate = baseDate.getDate() + '/' + (baseDate.getMonth() + 1) + '/' + baseDate.getFullYear();
        label = fullDate;
    } else if (mode === 'week') {
        var wStart = new Date(baseDate);
        var dayOfWeek = wStart.getDay();
        var diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        wStart = addDays(wStart, diff);
        var wEnd = addDays(wStart, 6);
        label = wStart.getDate() + '/' + (wStart.getMonth() + 1) + ' - ' + wEnd.getDate() + '/' + (wEnd.getMonth() + 1) + '/' + wEnd.getFullYear();
    } else if (mode === 'month') {
        var monthNames = {
            vi: ['Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6', 'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'],
            en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
        };
        var names = monthNames[lang] || monthNames.vi;
        label = names[baseDate.getMonth()] + ' ' + baseDate.getFullYear();
    }

    var modeLabels = {
        day: { vi: 'Ngày', en: 'Day', fr: 'Jour', es: 'Día', id: 'Hari', ja: '日', zh: '日', ko: '일', hi: 'दिन', ar: 'يوم' },
        week: { vi: 'Tuần', en: 'Week', fr: 'Semaine', es: 'Semana', id: 'Minggu', ja: '週', zh: '周', ko: '주', hi: 'सप्ताह', ar: 'أسبوع' },
        month: { vi: 'Tháng', en: 'Month', fr: 'Mois', es: 'Mes', id: 'Bulan', ja: '月', zh: '月', ko: '월', hi: 'महीना', ar: 'شهر' }
    };

    return (
        <div className="timeline-nav">
            <div className="nav-left">
                <button className="nav-btn" onClick={onPrev}>◀</button>
                <button className="nav-btn nav-today" onClick={onToday}>
                    {AntLocalization.translateText({ vi: 'Hôm nay', en: 'Today', es: 'Hoy', id: 'Hari ini', ja: '今日', zh: '今天', ko: '오늘', hi: 'आज', ar: 'اليوم', fr: 'Ce jour' })}
                </button>
                <button className="nav-btn" onClick={onNext}>▶</button>
                <span className="nav-label">{label}</span>
            </div>
            {modes && modes.length > 1 && (
                <div className="nav-modes">
                    {modes.map(function (m) {
                        var mLabel = modeLabels[m] || { vi: m, en: m };
                        return (
                            <button
                                key={m}
                                className={'mode-btn' + (m === mode ? ' active' : '')}
                                onClick={function () { onModeChange(m); }}
                            >
                                {AntLocalization.translateText(mLabel)}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── Empty Slot (background grid cell) ────────────────────────
function EmptySlotOverlay({ columns, resourceId, onSlotClick, cellHeight }) {
    if (!onSlotClick) return null;

    return (
        <div className="slot-overlay">
            {columns.map(function (col) {
                var style = {
                    left: (columns.indexOf(col) / columns.length * 100) + '%',
                    width: (100 / columns.length) + '%',
                    height: cellHeight ? cellHeight + 'px' : '100%'
                };
                return (
                    <div
                        key={col.key}
                        className={'slot-cell' + (col.isToday ? ' today' : '') + (col.isWeekend ? ' weekend' : '')}
                        style={style}
                        onClick={function () {
                            onSlotClick({
                                resourceId: resourceId,
                                date: col.date || null,
                                startHour: col.start || null,
                                endHour: col.end || null,
                                column: col
                            });
                        }}
                    />
                );
            })}
        </div>
    );
}

// ─── Mock Data ───────────────────────────────────────────────────
var MOCK_RESOURCES = [
    { id: 'staff-1', name: 'Nguyễn Thị Lan', group: 'Stylist' },
    { id: 'staff-2', name: 'Trần Văn Minh', group: 'Stylist' },
    { id: 'staff-3', name: 'Phạm Hồng Anh', group: 'Therapist' },
    { id: 'staff-4', name: 'Lê Thúy Hằng', group: 'Therapist' },
    { id: 'room-1', name: 'Phòng VIP 1', group: 'VIP' },
    { id: 'room-2', name: 'Phòng VIP 2', group: 'VIP' },
    { id: 'room-3', name: 'Phòng thường 1', group: 'Standard' }
];

var MOCK_BOOKINGS = (function () {
    var today = new Date(); today.setHours(0, 0, 0, 0);
    function fmtDate(d) { return d.toISOString().split('T')[0]; }
    var todayStr = fmtDate(today);
    var d_3 = fmtDate(addDays(today, -3));
    var d_2 = fmtDate(addDays(today, -2));
    var d_1 = fmtDate(addDays(today, -1));
    var d1 = fmtDate(addDays(today, 1));
    var d2 = fmtDate(addDays(today, 2));
    var d3 = fmtDate(addDays(today, 3));

    return [
        // ── 3 days ago ──
        { id: 'b-3a', resource_id: 'staff-1', start_hour: 9, end_hour: 10.5, date: d_3, display: 'Chị Trang - Uốn tóc', status: 'Close' },
        { id: 'b-3b', resource_id: 'staff-3', start_hour: 14, end_hour: 15.5, date: d_3, display: 'Anh Hùng - Massage toàn thân', status: 'Close' },
        // ── 2 days ago ──
        { id: 'b-2a', resource_id: 'staff-2', start_hour: 8, end_hour: 9.5, date: d_2, display: 'Chị Lan - Gội dưỡng', status: 'Close' },
        { id: 'b-2b', resource_id: 'staff-4', start_hour: 10, end_hour: 11.5, date: d_2, display: 'Anh Phong - Trị mụn', status: 'Close' },
        { id: 'b-2c', resource_id: 'room-1', start_hour: 13, end_hour: 15, date: d_2, display: 'Chị Hạnh - VIP Facial', status: 'Close' },
        // ── Yesterday ──
        { id: 'b-1a', resource_id: 'staff-1', start_hour: 8.5, end_hour: 10, date: d_1, display: 'Chị Dung - Nhuộm highlight', status: 'Close' },
        { id: 'b-1b', resource_id: 'staff-2', start_hour: 13, end_hour: 14, date: d_1, display: 'Anh Bảo - Cắt tóc nam', status: 'Close' },
        { id: 'b-1c', resource_id: 'room-2', start_hour: 9, end_hour: 11, date: d_1, display: 'Chị Uyên - Trị liệu VIP', status: 'Close' },
        // ── Today ──
        { id: 'b1', resource_id: 'staff-1', start_hour: 8, end_hour: 9.5, date: todayStr, display: 'Chị Mai - Gội đầu', status: 'Close' },
        { id: 'b2', resource_id: 'staff-1', start_hour: 10, end_hour: 11.5, date: todayStr, display: 'Anh Tuấn - Cắt tóc', status: 'Open' },
        { id: 'b3', resource_id: 'staff-2', start_hour: 9, end_hour: 10.5, date: todayStr, display: 'Chị Hoa - Nhuộm tóc', status: 'Release' },
        { id: 'b4', resource_id: 'staff-3', start_hour: 8.5, end_hour: 10, date: todayStr, display: 'Anh Long - Massage', status: 'Open' },
        { id: 'b5', resource_id: 'staff-3', start_hour: 13, end_hour: 14.5, date: todayStr, display: 'Chị Linh - Facial', status: 'New' },
        { id: 'b6', resource_id: 'room-1', start_hour: 9, end_hour: 11, date: todayStr, display: 'Chị Ngọc - VIP Spa', status: 'Open' },
        { id: 'b7', resource_id: 'room-2', start_hour: 14, end_hour: 16, date: todayStr, display: 'Anh Khoa - Trị liệu', status: 'Release' },
        { id: 'b8', resource_id: 'staff-4', start_hour: 11, end_hour: 12, date: todayStr, display: 'Chị Yến - Chăm sóc da', status: 'Close' },
        // ── Tomorrow ──
        { id: 'b+1a', resource_id: 'staff-1', start_hour: 9, end_hour: 10.5, date: d1, display: 'Chị Thảo - Duỗi tóc', status: 'New' },
        { id: 'b+1b', resource_id: 'staff-3', start_hour: 10, end_hour: 11.5, date: d1, display: 'Anh Đức - Massage vai gáy', status: 'New' },
        { id: 'b+1c', resource_id: 'room-1', start_hour: 14, end_hour: 16, date: d1, display: 'Chị Phương - VIP Package', status: 'New' },
        // ── 2 days from now ──
        { id: 'b+2a', resource_id: 'staff-2', start_hour: 8, end_hour: 9, date: d2, display: 'Anh Nam - Cắt gội', status: 'New' },
        { id: 'b+2b', resource_id: 'staff-4', start_hour: 13, end_hour: 14.5, date: d2, display: 'Chị Nga - Chăm sóc da mặt', status: 'New' },
        // ── 3 days from now ──
        { id: 'b+3a', resource_id: 'room-2', start_hour: 10, end_hour: 12, date: d3, display: 'Anh Kiên - Trị liệu cổ vai', status: 'New' }
    ];
})();

// ═══════════════════════════════════════════════════════════════
// ─── MAIN COMPONENT ──────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════
export default function AntResourceTimeline(props) {
    var {
        // Data
        API,
        resourceAPI,
        resources: resourcesProp,
        items: itemsProp,

        // Field mapping
        resourceField,
        dateField,
        startField,
        endField,
        statusField,
        displayField,
        secondaryField,

        // Config
        mode: modeProp = 'day',
        modes = ['day', 'week', 'month'],
        date: dateProp,
        startHour = 7,
        endHour = 22,
        slotMinutes = 60,
        cellHeight = 52,

        // Display
        statusColors,
        groupBy,
        showOccupancy = false,
        showCurrentTime = true,
        compact = false,
        height,
        emptyText,

        // Tooltip
        tooltipFields,

        // Interaction
        onBlockClick,
        onSlotClick,

        // Filtering
        searchConditions,

        // Mock & Visibility
        mockMode = false,
        visible,

        // Session
        session
    } = props;

    var [mode, setMode] = useState(modeProp);
    var [baseDate, setBaseDate] = useState(dateProp ? new Date(dateProp) : new Date());
    var [resources, setResources] = useState(resourcesProp || []);
    var [items, setItems] = useState(itemsProp || []);
    var [loading, setLoading] = useState(false);
    var [collapsedGroups, setCollapsedGroups] = useState({});

    // ─── Sync props ───────────────────────────────────────────
    useEffect(function () {
        if (resourcesProp) setResources(resourcesProp);
    }, [resourcesProp]);

    useEffect(function () {
        if (itemsProp) setItems(itemsProp);
    }, [itemsProp]);

    useEffect(function () {
        if (modeProp) setMode(modeProp);
    }, [modeProp]);

    useEffect(function () {
        if (dateProp) setBaseDate(new Date(dateProp));
    }, [dateProp]);

    // ─── Load resources from API ──────────────────────────────
    useEffect(function () {
        if (mockMode) { setResources(MOCK_RESOURCES); return; }
        if (resourcesProp || !resourceAPI) return;
        var cancelled = false;

        (async function () {
            try {
                var result = await resourceAPI.getList({});
                if (!cancelled && result && result.rows) {
                    var mapped = result.rows.map(function (r) {
                        return {
                            id: r.id,
                            code: r.code,
                            name: r.name || r.code,
                            group: groupBy ? r[groupBy] : null,
                            avatar: r.avatar_short_path || null,
                            tags: r.tags || null,
                            _raw: r
                        };
                    });
                    setResources(mapped);
                }
            } catch (e) {
                // silent
            }
        })();

        return function () { cancelled = true; };
    }, [resourceAPI, resourcesProp, groupBy, mockMode]);

    // ─── Load bookings from API ───────────────────────────────
    useEffect(function () {
        if (mockMode) { setItems(MOCK_BOOKINGS); return; }
        if (itemsProp || !API) return;
        var cancelled = false;

        (async function () {
            setLoading(true);
            try {
                var params = {};
                if (searchConditions) params.searchConditions = Object.assign({}, searchConditions);
                else params.searchConditions = {};

                // Exclude cancelled by default
                if (statusField && !params.searchConditions[statusField]) {
                    params.searchConditions[statusField] = { type: '!=', value: 'Cancel' };
                }

                // Filter by date range based on mode
                if (mode === 'day') {
                    var dayStr = baseDate.getFullYear() + '-' +
                        ((baseDate.getMonth() + 1) < 10 ? '0' : '') + (baseDate.getMonth() + 1) + '-' +
                        (baseDate.getDate() < 10 ? '0' : '') + baseDate.getDate();
                    // Use explicit dateField prop, or derive from startField
                    var dayField = dateField || null;
                    if (!dayField) {
                        if (startField && startField.indexOf('date') >= 0) {
                            dayField = startField;
                        } else if (startField) {
                            dayField = startField.replace('_hour', '_date').replace('_time', '_date');
                            if (dayField === startField) dayField = 'appointment_date';
                        }
                    }
                    if (dayField) params.searchConditions[dayField] = dayStr;
                } else if (mode === 'week') {
                    var wStart = new Date(baseDate);
                    var dayOfWeek = wStart.getDay();
                    var diff2 = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                    wStart = addDays(wStart, diff2);
                    var wEnd = addDays(wStart, 6);
                    var weekDateField = dateField || startField;

                    if (endField) {
                        // Range overlap: start <= weekEnd AND end >= weekStart
                        params.searchConditions[startField] = { type: '<=', value: wEnd.toISOString().split('T')[0] };
                        params.searchConditions[endField] = { type: '>=', value: wStart.toISOString().split('T')[0] };
                    } else {
                        // No endField — filter by date field within week range
                        params.searchConditions[weekDateField] = {
                            type: 'between',
                            from: wStart.toISOString().split('T')[0],
                            to: wEnd.toISOString().split('T')[0]
                        };
                    }
                } else if (mode === 'month') {
                    var mStart = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
                    var mEnd = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);
                    var monthDateField = dateField || startField;

                    if (endField) {
                        params.searchConditions[startField] = { type: '<=', value: mEnd.toISOString().split('T')[0] };
                        params.searchConditions[endField] = { type: '>=', value: mStart.toISOString().split('T')[0] };
                    } else {
                        // No endField — filter by date field within month range
                        params.searchConditions[monthDateField] = {
                            type: 'between',
                            from: mStart.toISOString().split('T')[0],
                            to: mEnd.toISOString().split('T')[0]
                        };
                    }
                }

                params.pageSize = 500;
                var result = await API.getList(params);
                if (!cancelled && result && result.rows) {
                    setItems(result.rows);
                }
            } catch (e) {
                // silent
            }
            if (!cancelled) setLoading(false);
        })();

        return function () { cancelled = true; };
    }, [API, itemsProp, mode, baseDate, dateField, startField, endField, statusField, searchConditions, mockMode]);

    // ─── Auto-scroll to current time in day mode ─────────────
    var timelineBodyRef = useRef(null);

    useEffect(function () {
        if (mode !== 'day') return;
        var el = timelineBodyRef.current;
        if (!el) return;
        var now = new Date();
        var currentH = now.getHours() + now.getMinutes() / 60;
        if (currentH < startHour || currentH > endHour) return;
        var pct = (currentH - startHour) / (endHour - startHour);
        // Wait a tick for layout to complete
        var timer = setTimeout(function () {
            if (!el.scrollWidth || !el.clientWidth) return;
            var targetX = pct * el.scrollWidth - el.clientWidth / 2;
            el.scrollLeft = Math.max(0, targetX);
        }, 50);
        return function () { clearTimeout(timer); };
    }, [mode, baseDate, startHour, endHour]);

    // ─── Generate columns ─────────────────────────────────────
    var columns = useMemo(function () {
        if (mode === 'day') return generateDayColumns(startHour, endHour, slotMinutes);
        if (mode === 'week') return generateWeekColumns(baseDate);
        if (mode === 'month') return generateMonthColumns(baseDate);
        return [];
    }, [mode, baseDate, startHour, endHour, slotMinutes]);

    // ─── Map items to resources ───────────────────────────────
    var resourceItemsMap = useMemo(function () {
        var map = {};
        if (!items || !resourceField) return map;

        for (var i = 0; i < items.length; i++) {
            var item = items[i];
            var rKey = item[resourceField] || '_unassigned';
            if (!map[rKey]) map[rKey] = [];

            // Enrich with status info
            var enriched = Object.assign({}, item);
            if (statusField) {
                enriched._statusValue = item[statusField] || '';
                // Try to get display label from status
                enriched._statusLabel = item[statusField] || '';
            }
            map[rKey].push(enriched);
        }
        return map;
    }, [items, resourceField, statusField]);

    // ─── Booking counts & occupancy ───────────────────────────
    var bookingCounts = useMemo(function () {
        var counts = {};
        for (var key in resourceItemsMap) {
            counts[key] = resourceItemsMap[key].length;
        }
        return counts;
    }, [resourceItemsMap]);

    var occupancyMap = useMemo(function () {
        if (!showOccupancy) return null;
        var map = {};
        // Simple occupancy: count of bookings relative to available slots
        var totalSlots = columns.length;
        if (totalSlots === 0) return map;

        for (var key in resourceItemsMap) {
            var bookings = resourceItemsMap[key];
            // Rough calculation: sum of block widths
            var totalOccupied = 0;
            for (var i = 0; i < bookings.length; i++) {
                var pos = calcBlockPosition(bookings[i], columns, mode, startField, endField, startHour, endHour);
                if (pos) totalOccupied += pos.width;
            }
            map[key] = Math.min(totalOccupied, 100);
        }
        return map;
    }, [showOccupancy, resourceItemsMap, columns, mode, startField, endField, startHour, endHour]);

    // ─── Visible resources (respect collapsed groups) ─────────
    var visibleResources = useMemo(function () {
        if (!groupBy) return resources;
        var result = [];
        var currentGroup = null;

        for (var i = 0; i < resources.length; i++) {
            var r = resources[i];
            var gKey = r[groupBy] || r.group || '';
            if (typeof gKey === 'object') gKey = AntLocalization.translateText(gKey) || '';
            if (gKey !== currentGroup) {
                currentGroup = gKey;
                if (collapsedGroups[gKey]) {
                    // Skip all items in this group
                    while (i + 1 < resources.length) {
                        var nextG = resources[i + 1][groupBy] || resources[i + 1].group || '';
                        if (typeof nextG === 'object') nextG = AntLocalization.translateText(nextG) || '';
                        if (nextG !== gKey) break;
                        i++;
                    }
                    continue;
                }
            }
            result.push(r);
        }
        return result;
    }, [resources, groupBy, collapsedGroups]);

    // ─── Navigation ───────────────────────────────────────────
    var handlePrev = useCallback(function () {
        setBaseDate(function (d) {
            if (mode === 'day') return addDays(d, -1);
            if (mode === 'week') return addDays(d, -7);
            if (mode === 'month') return addMonths(d, -1);
            return d;
        });
    }, [mode]);

    var handleNext = useCallback(function () {
        setBaseDate(function (d) {
            if (mode === 'day') return addDays(d, 1);
            if (mode === 'week') return addDays(d, 7);
            if (mode === 'month') return addMonths(d, 1);
            return d;
        });
    }, [mode]);

    var handleToday = useCallback(function () {
        setBaseDate(new Date());
    }, []);

    var handleModeChange = useCallback(function (newMode) {
        setMode(newMode);
    }, []);

    var handleToggleGroup = useCallback(function (groupName) {
        setCollapsedGroups(function (prev) {
            var next = Object.assign({}, prev);
            next[groupName] = !next[groupName];
            return next;
        });
    }, []);

    // ─── Default status colors ────────────────────────────────
    var colors = useMemo(function () {
        return statusColors || {
            'New': '#3B82F6',
            'Release': '#8B5CF6',
            'Open': '#F59E0B',
            'Close': '#10B981',
            'Cancel': '#9CA3AF',
            'Closed': '#10B981',
            'Released': '#8B5CF6',
            'Cancelled': '#9CA3AF'
        };
    }, [statusColors]);

    // ─── Default tooltip fields ───────────────────────────────
    var tipFields = useMemo(function () {
        return tooltipFields || [
            { dataField: displayField || 'name', label: { vi: 'Tên', en: 'Name', fr: 'Nom', es: 'Nombre', id: 'Nama', ja: '名前', zh: '名称', ko: '이름', hi: 'नाम', ar: 'الاسم' } },
            { dataField: 'entity_name', label: { vi: 'Khách hàng', en: 'Customer', fr: 'Client', es: 'Cliente', id: 'Pelanggan', ja: '顧客', zh: '客户', ko: '고객', hi: 'ग्राहक', ar: 'العميل' } },
            { dataField: 'entity_contact_phone', label: { vi: 'SĐT', en: 'Phone', fr: 'Tél.', es: 'Tel.', id: 'Telp', ja: '電話', zh: '电话', ko: '전화', hi: 'फ़ोन', ar: 'هاتف' } },
            { dataField: 'description', label: { vi: 'Ghi chú', en: 'Note', fr: 'Note', es: 'Nota', id: 'Catatan', ja: 'メモ', zh: '备注', ko: '메모', hi: 'नोट', ar: 'ملاحظة' } }
        ];
    }, [tooltipFields, displayField]);

    // ─── Empty state ──────────────────────────────────────────
    var isEmpty = resources.length === 0 && !loading;

    // ─── Container style ──────────────────────────────────────
    var containerStyle = {};
    if (height) containerStyle.maxHeight = height;

    // ─── Visibility check (after all hooks) ────────────────────
    if (visible && session && !AntCondition.checkAll({}, visible, session)) return null;

    // ─── Render ───────────────────────────────────────────────
    return (
        <div className={'ant-resource-timeline' + (compact ? ' compact' : '') + ' mode-' + mode}>
            {/* Navigation */}
            <TimelineNav
                mode={mode}
                baseDate={baseDate}
                onPrev={handlePrev}
                onNext={handleNext}
                onToday={handleToday}
                onModeChange={handleModeChange}
                modes={modes}
            />

            {/* Loading */}
            {loading && items.length === 0 && (
                <div className="timeline-loading">
                    {AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '불러오는 중...', hi: 'लोड हो रहा...', ar: 'تحميل...' })}
                </div>
            )}

            {/* Empty */}
            {isEmpty && (
                <div className="timeline-empty">
                    {emptyText
                        ? (typeof emptyText === 'object' ? AntLocalization.translateText(emptyText) : emptyText)
                        : AntLocalization.translateText({ vi: 'Chưa có dữ liệu', en: 'No data', fr: 'Aucune donnée', es: 'Sin datos', id: 'Belum ada data', ja: 'データなし', zh: '暂无数据', ko: '데이터 없음', hi: 'कोई डेटा नहीं', ar: 'لا توجد بيانات' })}
                </div>
            )}

            {/* Main grid */}
            {resources.length > 0 && (
                <div className="timeline-container" style={containerStyle}>
                    <div className="timeline-grid">
                        {/* Left panel: Resources */}
                        <ResourcePanel
                            resources={visibleResources}
                            groupBy={groupBy}
                            bookingCounts={bookingCounts}
                            occupancyMap={occupancyMap}
                            showOccupancy={showOccupancy}
                            collapsedGroups={collapsedGroups}
                            onToggleGroup={handleToggleGroup}
                            cellHeight={cellHeight}
                        />

                        {/* Right panel: Timeline grid */}
                        <div className="timeline-body" ref={timelineBodyRef}>
                            {/* Column headers */}
                            <div className="timeline-header">
                                {columns.map(function (col) {
                                    var cls = 'col-header';
                                    if (col.isToday) cls += ' today';
                                    if (col.isWeekend) cls += ' weekend';
                                    if (col.isNow) cls += ' now';
                                    return (
                                        <div key={col.key} className={cls} style={{ width: (100 / columns.length) + '%' }}>
                                            {mode === 'month' && col.dayOfWeek != null && (
                                                <span className="col-day-name">
                                                    {['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'][col.dayOfWeek]}
                                                </span>
                                            )}
                                            <span className="col-label">{col.label}</span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Resource rows */}
                            <div className="timeline-rows">
                                {visibleResources.map(function (resource) {
                                    var rId = resource.id || resource.code;
                                    var rItems = resourceItemsMap[rId] || resourceItemsMap[resource.code] || [];

                                    return (
                                        <div key={rId} className="timeline-row" style={{ height: cellHeight + 'px' }}>
                                            {/* Grid lines */}
                                            <div className="row-grid">
                                                {columns.map(function (col) {
                                                    var cls = 'grid-cell';
                                                    if (col.isToday) cls += ' today';
                                                    if (col.isWeekend) cls += ' weekend';
                                                    return (
                                                        <div
                                                            key={col.key}
                                                            className={cls}
                                                            style={{ width: (100 / columns.length) + '%' }}
                                                        />
                                                    );
                                                })}
                                            </div>

                                            {/* Clickable empty slots */}
                                            <EmptySlotOverlay
                                                columns={columns}
                                                resourceId={rId}
                                                onSlotClick={onSlotClick}
                                                cellHeight={cellHeight}
                                            />

                                            {/* Booking blocks */}
                                            {rItems.map(function (item) {
                                                var pos = calcBlockPosition(item, columns, mode, startField, endField, startHour, endHour);
                                                if (!pos) return null;
                                                return (
                                                    <TimelineBlock
                                                        key={item.id || item.code}
                                                        item={item}
                                                        left={pos.left}
                                                        width={pos.width}
                                                        statusColors={colors}
                                                        displayField={displayField || 'entity_name'}
                                                        secondaryField={secondaryField}
                                                        tooltipFields={tipFields}
                                                        onClick={onBlockClick}
                                                        cellHeight={cellHeight}
                                                    />
                                                );
                                            })}

                                            {/* Current time line */}
                                            {showCurrentTime && (
                                                <CurrentTimeIndicator
                                                    mode={mode}
                                                    columns={columns}
                                                    startHour={startHour}
                                                    endHour={endHour}
                                                    baseDate={baseDate}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Legend */}
            {resources.length > 0 && (
                <div className="timeline-legend">
                    {Object.keys(colors).map(function (key) {
                        return (
                            <div key={key} className="legend-item">
                                <span className="legend-dot" style={{ background: colors[key] }} />
                                <span className="legend-label">{key}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
